import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Pencil, 
  Play, 
  Settings, 
  Clock, 
  Tag, 
  User, 
  Globe, 
  Lock,
  Eye,
  Code,
  Database,
  Webhook,
  Mail,
  Calendar,
  Image,
  MessageSquare,
  BarChart3,
  FileText,
  Cloud,
  Terminal,
  Cpu,
  Puzzle,
  Search,
  Zap,
  Shield,
  Users,
  Copy,
  CheckCircle
} from 'lucide-react';
import { Button } from "../components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { useToast } from "../components/ui/Toast";
import { Spinner } from "../components/ui/Spinner";
import { useToolsApi } from '../apis/tools-apis';
import { useUsersApi } from '../apis/users-apis';
import { Tool } from '../types';
import { useAuth } from '../contexts/AuthContext';

// Tool icon mapping (same as ToolsList)
const getToolIcon = (tool: Tool) => {
  const name = tool.toolName.toLowerCase();
  const type = tool.toolType?.toLowerCase() || '';
  
  if (name.includes('search') || name.includes('google') || name.includes('bing')) return Search;
  if (name.includes('database') || name.includes('sql') || name.includes('db')) return Database;
  if (name.includes('api') || name.includes('webhook') || name.includes('http')) return Webhook;
  if (name.includes('email') || name.includes('mail') || name.includes('smtp')) return Mail;
  if (name.includes('calendar') || name.includes('schedule') || name.includes('date')) return Calendar;
  if (name.includes('image') || name.includes('photo') || name.includes('picture')) return Image;
  if (name.includes('chat') || name.includes('message') || name.includes('slack')) return MessageSquare;
  if (name.includes('analytics') || name.includes('chart') || name.includes('graph')) return BarChart3;
  if (name.includes('file') || name.includes('document') || name.includes('pdf')) return FileText;
  if (name.includes('cloud') || name.includes('aws') || name.includes('azure')) return Cloud;
  if (name.includes('code') || name.includes('git') || name.includes('github')) return Code;
  if (name.includes('terminal') || name.includes('shell') || name.includes('command')) return Terminal;
  if (name.includes('cpu') || name.includes('process') || name.includes('system')) return Cpu;
  
  return Puzzle;
};

const ViewToolPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { addToast } = useToast();
  const toolsApi = useToolsApi();
  const usersApi = useUsersApi();
  const { user, isAdmin } = useAuth();
  
  const [tool, setTool] = useState<Tool | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [ownerEmail, setOwnerEmail] = useState<string | null>(null);

  const fetchTool = useCallback(async () => {
    if (!id) return;
    
    try {
      const fetchedTool = await toolsApi.getTool(id);
      
      if (fetchedTool) {
        setTool(fetchedTool);
        // Fetch owner email if different from current user
        try {
          if (fetchedTool.ownerId && fetchedTool.ownerId !== user?.sub) {
            const ownerData = await usersApi.getUser(fetchedTool.ownerId);
            setOwnerEmail(ownerData.email);
          } else {
            setOwnerEmail(user?.email || null);
          }
        } catch {
          setOwnerEmail(null);
        }
      } else {
        addToast({
          title: "Error",
          description: "Tool not found",
          variant: "destructive",
        });
        navigate('/studio/tools');
      }
    } catch (error) {
      console.error('Error fetching tool:', error);
      addToast({
        title: "Error",
        description: "Failed to load tool details",
        variant: "destructive",
      });
      navigate('/studio/tools');
    } finally {
      setIsLoading(false);
    }
  }, [id, toolsApi, addToast, navigate, usersApi, user]);

  useEffect(() => {
    fetchTool();
  }, [fetchTool]);

  const handleCopyToClipboard = async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(fieldName);
      setTimeout(() => setCopiedField(null), 2000);
      addToast({
        title: "Copied",
        description: `${fieldName} copied to clipboard`,
        variant: "default",
      });
    } catch (error) {
      addToast({
        title: "Error",
        description: "Failed to copy to clipboard",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <Spinner className="w-8 h-8 text-blue-500" />
        <p className="text-gray-600">Loading tool details...</p>
      </div>
    );
  }

  if (!tool) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="bg-gradient-to-br from-red-100 to-red-200 p-4 rounded-full">
          <Eye className="h-8 w-8 text-red-600" />
        </div>
        <div className="text-center">
          <h3 className="text-xl font-semibold text-gray-900 mb-2">Tool Not Found</h3>
          <p className="text-gray-600 mb-4">The requested tool could not be found.</p>
          <Button onClick={() => navigate('/studio/tools')}>
            Back to Tools
          </Button>
        </div>
      </div>
    );
  }

  const isOwned = tool.ownerId === user?.sub;
  const canEdit = isOwned || isAdmin;
  const ToolIcon = getToolIcon(tool);

  return (
    <div className="container mx-auto p-6 max-w-6xl font-['Poppins','SF_Pro_Display','system-ui','-apple-system','sans-serif'] antialiased">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/studio/tools')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 px-3 py-2 rounded-lg transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Tools</span>
            </Button>
          </div>
          
          <div className="flex items-center gap-3">
            <Button
              onClick={() => navigate(`/studio/tools/test/${tool.toolId}`)}
              className="group relative bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-700 hover:to-emerald-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-6 py-3 rounded-xl font-semibold"
            >
              <div className="flex items-center gap-2">
                <div className="bg-white/20 p-1 rounded-lg group-hover:bg-white/30 transition-colors">
                  <Play className="h-4 w-4" />
                </div>
                <span>Test Tool</span>
              </div>
            </Button>
            
            {canEdit && (
              <Button
                onClick={() => navigate(`/studio/tools/edit/${tool.toolId}`)}
                className="group relative bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-6 py-3 rounded-xl font-semibold"
              >
                <div className="flex items-center gap-2">
                  <div className="bg-white/20 p-1 rounded-lg group-hover:bg-white/30 transition-colors">
                    <Pencil className="h-4 w-4" />
                  </div>
                  <span>Edit Tool</span>
                </div>
              </Button>
            )}
          </div>
        </div>

        {/* Tool Header Card */}
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 shadow-lg">
          <CardContent className="p-8">
            <div className="flex items-center gap-6">
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-2xl shadow-lg">
                <ToolIcon className="h-10 w-10 text-white" />
              </div>
              
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
                    {tool.toolName}
                  </h1>
                  {isOwned && (
                    <Badge className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white shadow-sm">
                      <User className="w-3 h-3 mr-1" />
                      Owned
                    </Badge>
                  )}
                  {tool.isCore && (
                    <Badge className="bg-gradient-to-r from-violet-500 to-violet-600 text-white shadow-sm">
                      <Shield className="w-3 h-3 mr-1" />
                      Core Tool
                    </Badge>
                  )}
                </div>
                
                <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                  {tool.toolDescription || 'No description provided.'}
                </p>
                
                <div className="flex items-center gap-6 text-sm text-gray-600">
                  <span className="flex items-center gap-2 font-medium">
                    <Settings className="w-4 h-4 text-blue-500" />
                    {tool.toolParameters?.parameters?.length || 0} parameters
                  </span>
                  <span className="flex items-center gap-2 font-medium">
                    <Clock className="w-4 h-4 text-emerald-500" />
                    {tool.toolTimeout || '30'}s timeout
                  </span>
                  {tool.toolTags && tool.toolTags.length > 0 && (
                    <span className="flex items-center gap-2 font-medium">
                      <Tag className="w-4 h-4 text-violet-500" />
                      {tool.toolTags.length} tags
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Main Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Configuration Section */}
          <Card className="shadow-lg border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Settings className="h-5 w-5 text-blue-600" />
                Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
                             <div>
                 <label className="text-sm font-semibold text-gray-700 mb-2 block">API Endpoint</label>
                 <div className="flex items-start gap-2">
                   <code className="flex-1 bg-gray-100 px-3 py-2 rounded-lg text-sm font-mono text-gray-800 border break-all overflow-wrap-anywhere min-w-0">
                     {tool.toolUrlEndpoint || 'Not specified'}
                   </code>
                   {tool.toolUrlEndpoint && (
                     <Button
                       variant="ghost"
                       size="sm"
                       onClick={() => handleCopyToClipboard(tool.toolUrlEndpoint, 'API Endpoint')}
                       className="h-8 w-8 p-0 hover:bg-gray-100 flex-shrink-0 mt-0.5"
                     >
                       {copiedField === 'API Endpoint' ? (
                         <CheckCircle className="h-4 w-4 text-green-600" />
                       ) : (
                         <Copy className="h-4 w-4 text-gray-500" />
                       )}
                     </Button>
                   )}
                 </div>
               </div>
              
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">Authentication</label>
                <div className="bg-gray-100 px-3 py-2 rounded-lg text-sm text-gray-800 border">
                  {tool.toolAuthentication ? (
                    <span className="flex items-center gap-2">
                      <Lock className="h-4 w-4 text-green-600" />
                      Authentication configured
                    </span>
                  ) : (
                    <span className="flex items-center gap-2 text-gray-500">
                      <Globe className="h-4 w-4" />
                      No authentication required
                    </span>
                  )}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">Timeout</label>
                <div className="bg-gray-100 px-3 py-2 rounded-lg text-sm text-gray-800 border">
                  {tool.toolTimeout || '30'} seconds
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Parameters Section */}
          <Card className="shadow-lg border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Code className="h-5 w-5 text-emerald-600" />
                Input Parameters ({tool.toolParameters?.parameters?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {tool.toolParameters?.parameters && tool.toolParameters.parameters.length > 0 ? (
                <div className="space-y-4">
                  {tool.toolParameters.parameters.map((param, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-semibold text-gray-900">{param.key}</h4>
                        <div className="flex items-center gap-2">
                          <Badge variant="secondary" className="text-xs">
                            {param.type}
                          </Badge>
                          
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mb-2">
                        {param.description || 'No description provided'}
                      </p>
                                             {param.example && (
                         <div className="text-xs text-gray-500">
                           <span className="font-medium">Example:</span> {param.example}
                         </div>
                       )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <Code className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p>No input parameters defined</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Output Definition Section */}
          <Card className="shadow-lg border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Database className="h-5 w-5 text-violet-600" />
                Output Definition
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Description</label>
                  <div className="bg-gray-100 px-3 py-2 rounded-lg text-sm text-gray-800 border">
                    {tool.toolOutput?.parameters?.output?.description || 'No description provided'}
                  </div>
                </div>
                
                <div>
                  <label className="text-sm font-semibold text-gray-700 mb-2 block">Type</label>
                  <Badge variant="secondary" className="text-sm">
                    {tool.toolOutput?.parameters?.output?.type || 'str'}
                  </Badge>
                </div>
                
                
              </div>
            </CardContent>
          </Card>

          {/* Annotation Section */}
          <Card className="shadow-lg border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <FileText className="h-5 w-5 text-orange-600" />
                Tool Annotation
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              {tool.toolAnnotation ? (
                <div className="bg-gray-100 px-4 py-3 rounded-lg text-sm text-gray-800 border leading-relaxed whitespace-pre-wrap">
                  {tool.toolAnnotation}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FileText className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                  <p>No annotation provided</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Metadata & Access Control */}
        <div className="space-y-6">
          {/* Tags Section */}
          {tool.toolTags && tool.toolTags.length > 0 && (
            <Card className="shadow-lg border-gray-200">
              <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <CardTitle className="flex items-center gap-2 text-gray-900">
                  <Tag className="h-5 w-5 text-violet-600" />
                  Tags
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="flex flex-wrap gap-2">
                  {tool.toolTags.map((tag, index) => (
                    <Badge
                      key={index}
                      variant="secondary"
                      className="bg-violet-100 text-violet-700 hover:bg-violet-200 transition-colors"
                    >
                      {tag}
                    </Badge>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Access Control Section */}
          <Card className="shadow-lg border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Users className="h-5 w-5 text-blue-600" />
                Access Control
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">View Access</label>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-sm">
                    {tool.accessControl?.viewAccessType || 'Owner Only'}
                  </Badge>
                                     {tool.accessControl?.viewAccessType === 'Allowed Users' && tool.accessControl.viewAllowedUsers && (
                     <span className="text-xs text-gray-500">
                       ({tool.accessControl.viewAllowedUsers.length} users)
                     </span>
                   )}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-semibold text-gray-700 mb-2 block">Edit Access</label>
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-sm">
                    {tool.accessControl?.editAccessType || 'Owner Only'}
                  </Badge>
                                     {tool.accessControl?.editAccessType === 'Allowed Users' && tool.accessControl.editAllowedUsers && (
                     <span className="text-xs text-gray-500">
                       ({tool.accessControl.editAllowedUsers.length} users)
                     </span>
                   )}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Metadata Section */}
          <Card className="shadow-lg border-gray-200">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
              <CardTitle className="flex items-center gap-2 text-gray-900">
                <Zap className="h-5 w-5 text-yellow-600" />
                Metadata
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-3">
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Tool ID</span>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">
                    {tool.toolId}
                  </code>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleCopyToClipboard(tool.toolId || '', 'Tool ID')}
                    className="h-6 w-6 p-0 hover:bg-gray-100"
                  >
                    {copiedField === 'Tool ID' ? (
                      <CheckCircle className="h-3 w-3 text-green-600" />
                    ) : (
                      <Copy className="h-3 w-3 text-gray-500" />
                    )}
                  </Button>
                </div>
              </div>
              
              <div className="flex justify-between items-center text-sm">
                <span className="text-gray-600">Owner</span>
                <span className="text-gray-900 font-medium break-all">
                  {isOwned ? (user?.email || 'You') : (ownerEmail || tool.ownerId)}
                </span>
              </div>
              
                             <div className="flex justify-between items-center text-sm">
                 <span className="text-gray-600">Type</span>
                 <span className="text-gray-900">
                   {tool.isCore ? 'Core Tool' : 'Custom Tool'}
                 </span>
               </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ViewToolPage;